/**
 * /faturas/nova — F-consolidated-invoice-review.
 *
 * Passo de REVISÃO antes de efetivar a fatura consolidada. O operador chega aqui
 * de /dossiers (botão "Emitir fatura", que passa os dossierIds na URL), confere a
 * fatura COMPLETA (emissor, cliente, tabela de dossiers, Total HT/TVA/TTC,
 * autoliquidação) e tem as opções: remover dossier (×), acrescentar dossiers,
 * adicionar email, cancelar, ou confirmar+emitir (e enviar por email se preenchido).
 *
 * Nada é persistido até "Confirmar e emitir" — o preview vem de
 * customerInvoices.previewConsolidated (read-only).
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { DueDateField } from "../components/form/DueDateField.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { InvoiceDraftModal } from "../components/domain/InvoiceDraftModal.js";

export default function FaturaNova() {
  const { user } = useAuth();
  const modals = useModals();
  const [, setLocation] = useLocation();
  const t = useT();

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId] = useSelectedCompany(accessibleCompanies);
  const issuer = accessibleCompanies.find((c) => c.id === companyId);

  const [dossierIds, setDossierIds] = useState<number[]>(() => {
    const raw = new URLSearchParams(window.location.search).get("dossierIds");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  });

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberTouched, setInvoiceNumberTouched] = useState(false);
  const [dueAt, setDueAt] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const previewQuery = trpc.customerInvoices.previewConsolidated.useQuery(
    { companyId: companyId ?? 0, dossierIds },
    { enabled: companyId !== null && dossierIds.length > 0 },
  );
  const preview = previewQuery.data;

  // Auto-preenche o email sugerido do cliente (até o operador editar).
  useEffect(() => {
    if (!emailTouched && preview?.customer?.suggestedEmail) {
      setEmail(preview.customer.suggestedEmail);
    }
  }, [preview?.customer?.suggestedEmail, emailTouched]);

  // F-invoice-number — pré-preenche o nº da fatura sugerido (segue o último
  // emitido pela empresa, preservando o padrão), até o operador editar.
  useEffect(() => {
    if (!invoiceNumberTouched && preview?.suggestedInvoiceNumber) {
      setInvoiceNumber(preview.suggestedInvoiceNumber);
    }
  }, [preview?.suggestedInvoiceNumber, invoiceNumberTouched]);

  const issueMut = trpc.customerInvoices.issueConsolidated.useMutation();
  // CEO 2026-10-27 — rascunho com conferência automática antes de emitir.
  const [draftId, setDraftId] = useState<number | null>(null);
  const createDraft = trpc.invoiceDrafts.create.useMutation({
    onSuccess: (r) => setDraftId(r.id),
    onError: (e) => {
      if (e.message.startsWith("DRAFT_EXISTS:")) {
        setDraftId(Number(e.message.slice("DRAFT_EXISTS:".length)));
        return;
      }
      setError(e.message);
    },
  });
  function criarRascunho() {
    if (companyId === null || !preview?.canIssue) return;
    setError(null);
    const blockedSet = new Set(preview.blockedDossierIds);
    const ids = dossierIds.filter((id) => !blockedSet.has(id));
    // O nº sugerido (próximo da série) não vai para o rascunho: só o digitado.
    const numero = invoiceNumberTouched ? invoiceNumber.trim() : "";
    createDraft.mutate({
      companyId,
      dossierIds: ids,
      ...(numero ? { invoiceNumber: numero } : {}),
      ...(dueAt ? { dueAt } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  }
  async function aposEmitirRascunho(invoiceId: number, numero: string) {
    if (companyId === null) return;
    let emailMsg = "";
    if (email.trim()) {
      try {
        await sendEmailMut.mutateAsync({ companyId, invoiceId, recipientEmail: email.trim() });
        emailMsg = ` ${t("invoiceNew.toastSentTo")} ${email.trim()}`;
      } catch (e) {
        emailMsg = ` (${t("invoiceNew.toastEmailFailed")}: ${e instanceof Error ? e.message : String(e)})`;
      }
    }
    setDraftId(null);
    await modals.alert({
      message: `${t("invoiceNew.toastInvoiceWord")} ${numero} ${t("invoiceNew.toastIssued")}${emailMsg}.`,
    });
    setLocation("/faturas");
  }
  const sendEmailMut = trpc.customerInvoices.sendEmail.useMutation();
  const utils = trpc.useUtils();

  function removeDossier(id: number) {
    setDossierIds((prev) => prev.filter((x) => x !== id));
  }

  async function confirmar(allowNumberJump = false) {
    if (companyId === null || !preview?.canIssue) return;
    setError(null);
    setPending(true);
    // Exclui dossiers bloqueados (já faturados) da emissão.
    const blockedSet = new Set(preview.blockedDossierIds);
    const ids = dossierIds.filter((id) => !blockedSet.has(id));
    try {
      const created = await issueMut.mutateAsync({
        companyId,
        dossierIds: ids,
        invoiceNumber: invoiceNumber.trim() || undefined,
        dueAt: dueAt || undefined,
        notes: notes.trim() || undefined,
        ...(allowNumberJump ? { allowNumberJump: true } : {}),
      });
      let emailMsg = "";
      if (email.trim()) {
        try {
          await sendEmailMut.mutateAsync({
            companyId,
            invoiceId: created.id,
            recipientEmail: email.trim(),
          });
          emailMsg = ` ${t("invoiceNew.toastSentTo")} ${email.trim()}`;
        } catch (e) {
          emailMsg = ` (${t("invoiceNew.toastEmailFailed")}: ${
            e instanceof Error ? e.message : String(e)
          })`;
        }
      }
      void utils.customerInvoices.list.invalidate();
      void utils.dossiers.invalidate();
      await modals.alert({
        message: `${t("invoiceNew.toastInvoiceWord")} ${created.invoiceNumber} ${t("invoiceNew.toastIssued")}${emailMsg}.`,
      });
      setLocation("/faturas");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // F-number-jump-guard — nº digitado pula a série: confirma antes.
      if (msg.startsWith("NUMBER_JUMP:")) {
        const [codigo, esperado, salto] = msg.slice("NUMBER_JUMP:".length).split("|");
        setPending(false);
        const go = await modals.confirm({
          title: t("invoiceNew.numberJumpTitle"),
          message: `${codigo} ${t("invoiceNew.numberJumpMid")} ${salto} ${t("invoiceNew.numberJumpMid2")} ${esperado}. ${t("invoiceNew.numberJumpAsk")}`,
          confirmLabel: t("invoiceNew.numberJumpConfirm"),
          tone: "danger",
        });
        if (go) await confirmar(true);
        return;
      }
      setError(msg);
    } finally {
      setPending(false);
    }
  }

  if (!user) return null;

  const cur = preview?.currency ?? "EUR";
  const activeItems = (preview?.items ?? []).filter((i) => !i.blocked);

  return (
    <Shell>
      {dossierIds.length === 0 ? (
        <p className="text-sm text-text-secondary">
          {t("invoiceNew.noDossierPre")}{" "}
          <a href="/dossiers" className="text-lime hover:underline">
            /dossiers
          </a>{" "}
          {t("invoiceNew.noDossierSuf")}
        </p>
      ) : (
        <>
          {previewQuery.isLoading && (
            <p className="text-sm text-text-muted">{t("invoiceNew.loadingPreview")}</p>
          )}
          {previewQuery.error && (
            <p className="text-sm text-red-400">{previewQuery.error.message}</p>
          )}

          {preview && preview.differentCustomers && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {t("invoiceNew.differentCustomersPre")}{" "}
              <b>{t("invoiceNew.differentCustomersBold")}</b>.{" "}
              {t("invoiceNew.differentCustomersSuf")}
            </div>
          )}

          {preview && (
            <>
              {/* Pré-visualização da fatura (emissor + cliente) */}
              <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-text-muted">
                      {t("invoiceNew.issuer")}
                    </p>
                    <p className="font-semibold">{issuer?.name ?? "—"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-text-muted">
                      {t("invoiceNew.customer")}
                    </p>
                    <p className="font-semibold">{preview.customer?.name ?? "—"}</p>
                  </div>
                </div>

                {/* Tabela de dossiers */}
                <table className="w-full text-xs sm:text-sm">
                  <thead className="text-[10px] uppercase tracking-normal text-text-muted border-b border-neutral-800 sm:text-xs sm:tracking-wider">
                    <tr>
                      <th className="hidden text-left py-2 sm:table-cell">
                        {t("invoiceNew.colDossier")}
                      </th>
                      <th className="text-left py-2">{t("invoiceNew.colPlate")}</th>
                      <th className="text-left py-2">{t("invoiceNew.colVehicle")}</th>
                      <th className="whitespace-nowrap text-right py-2 font-mono">
                        {t("invoiceNew.colTotalHt")}
                      </th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {preview.items.map((it) => (
                      <tr key={it.dossierId} className={it.blocked ? "opacity-50" : ""}>
                        <td className="hidden py-2 font-mono text-xs text-lime sm:table-cell">
                          {it.dossierCode}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-2 font-mono text-xs">
                          {it.plate ?? "—"}
                        </td>
                        <td className="py-2 text-xs">
                          {it.vehicle ?? "—"}
                          {it.blocked && (
                            <span className="ml-2 text-[10px] text-amber-300">
                              🔒 {t("invoiceNew.alreadyInvoicedBadge")}
                            </span>
                          )}
                          {!it.blocked && it.degarnissagePending && (
                            <span
                              className="mt-0.5 block w-fit rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300 sm:ml-2 sm:mt-0 sm:inline-block"
                              title={
                                it.degarnissageSemLinha
                                  ? t("invoiceNew.degarnPendingTitle")
                                  : t("invoiceNew.degarnOpenTitle")
                              }
                              data-testid="invoice-new-degarn"
                            >
                              ⚠{" "}
                              {it.degarnissageSemLinha
                                ? t("invoiceNew.degarnPendingBadge")
                                : t("invoiceNew.degarnOpenBadge")}{" "}
                              {formatMoney(it.degarnissageHt ?? 0, { decimals: 2 })}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 text-right font-mono">
                          {formatMoney(it.faturado, { decimals: 2 })}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeDossier(it.dossierId)}
                            title={t("invoiceNew.removeDossierTitle")}
                            className="text-red-300 hover:text-red-200"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totais */}
                <div className="flex flex-col items-end gap-1 text-sm border-t border-neutral-800 pt-3">
                  <Row
                    label={t("invoiceNew.colTotalHt")}
                    value={formatMoney(Number(preview.totalHt), { decimals: 2 })}
                  />
                  <Row
                    label={`TVA (${preview.tvaReverseCharge ? "0" : Number(preview.tvaRate).toFixed(0)}%)`}
                    value={formatMoney(Number(preview.tvaAmount), { decimals: 2 })}
                  />
                  <Row
                    label={t("invoiceNew.totalTtc")}
                    value={formatMoney(Number(preview.totalTtc), { decimals: 2 })}
                    bold
                  />
                  {preview.tvaReverseCharge && (
                    <p className="text-[11px] text-text-muted mt-1">
                      {t("invoiceNew.reverseChargeNote")}
                    </p>
                  )}
                </div>
              </div>

              {/* Opções: nº da fatura, email, vencimento, notas */}
              <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={t("invoiceNew.fieldInvoiceNumber")}>
                  <input
                    value={invoiceNumber}
                    onChange={(e) => {
                      setInvoiceNumber(e.target.value);
                      setInvoiceNumberTouched(true);
                    }}
                    placeholder={t("invoiceNew.invoiceNumberPlaceholder")}
                    className={`${inputClass} font-mono`}
                  />
                </Field>
                <Field label={t("invoiceNew.fieldEmail")}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailTouched(true);
                    }}
                    placeholder={t("invoiceNew.emailPlaceholder")}
                    className={inputClass}
                  />
                </Field>
                <Field label={t("invoiceNew.fieldDueDate")}>
                  <DueDateField
                    value={dueAt}
                    onChange={setDueAt}
                    inputClass={inputClass}
                    presetLabel={(d) => t(`invoiceNew.dueQuick${d}`)}
                  />
                </Field>
                <Field label={t("invoiceNew.fieldNotes")}>
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={2000}
                    className={inputClass}
                  />
                </Field>
              </div>

              {error && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 whitespace-pre-wrap">
                  {error}
                </div>
              )}

              {/* Ações */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLocation("/dossiers")}
                    className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
                  >
                    {t("invoiceNew.cancel")}
                  </button>
                  <a
                    href="/dossiers"
                    className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
                    title={t("invoiceNew.addDossiersTitle")}
                  >
                    {t("invoiceNew.addDossiers")}
                  </a>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void confirmar()}
                    disabled={pending || createDraft.isPending || !preview.canIssue}
                    className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime disabled:opacity-40 px-4 py-2 text-sm"
                    title={!preview.canIssue ? t("invoiceNew.cannotIssueTitle") : undefined}
                  >
                    {pending
                      ? t("invoiceNew.issuing")
                      : `${t("invoiceNew.confirmEmit")} (${activeItems.length} ${activeItems.length === 1 ? t("invoiceNew.serviceSingular") : t("invoiceNew.servicePlural")})`}
                  </button>
                  <button
                    type="button"
                    onClick={criarRascunho}
                    disabled={pending || createDraft.isPending || !preview.canIssue}
                    className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-5 py-2 text-sm font-semibold"
                    data-testid="invoice-new-draft"
                  >
                    {createDraft.isPending ? "…" : t("invoiceDraft.saveAsDraft")}
                  </button>
                </div>
              </div>
              {draftId !== null && companyId !== null && (
                <InvoiceDraftModal
                  companyId={companyId}
                  draftId={draftId}
                  onClose={() => setDraftId(null)}
                  onIssued={(id, numero) => void aposEmitirRascunho(id, numero)}
                  semEnvioAqui
                />
              )}
            </>
          )}
        </>
      )}
    </Shell>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-8 min-w-[220px]">
      <span className={bold ? "font-semibold" : "text-text-secondary"}>{label}</span>
      <span className={`font-mono tabular-nums ${bold ? "font-semibold text-lime" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-text-muted mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-5xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
